import { useEffect, useRef, useState } from "react";
import { Gift, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useBoideUser } from "@/hooks/use-boide-user";
import {
  bindMyReferrer,
  clearStoredReferral,
  getAffiliateMe,
  getBoideToken,
  getStoredReferral,
  referrerErrorReason,
} from "@/lib/boide-api";
import { isNever, setNever, takePending } from "@/lib/referral-prompt";

/**
 * Popup mời nhập mã giới thiệu NGAY SAU KHI ĐĂNG NHẬP, cho tài khoản chưa gắn mã.
 *
 * - Chỉ hiện sau một lượt đăng nhập thật (`markReferralPromptPending` gọi ở
 *   `login.tsx::finishLogin`), KHÔNG hiện mỗi lần mở app — người đã đăng nhập sẵn
 *   nhiều tháng không bị làm phiền mỗi lần vào trang.
 * - Chờ rời trang `/login` rồi mới xét: ở `/login` còn có hộp thoại "hoàn thiện hồ sơ",
 *   hai hộp thoại chồng nhau là người ta tắt luôn cả hai.
 * - "Nhắc lại sau" = hỏi lại ở LẦN ĐĂNG NHẬP SAU. Tick "Không hiện lại lần sau" rồi
 *   đóng = không bao giờ hỏi nữa (theo TÀI KHOẢN trên máy này — localStorage).
 * - `has_referrer` đọc từ `/v1/affiliate/me` sau khi `autoBindStoredReferral` đã chạy:
 *   ai vào bằng link `?ref=` thì đã được gắn sẵn và không thấy popup.
 */

const REASON_TEXT: Record<string, string> = {
  invalid: "Mã giới thiệu không tồn tại, bạn kiểm tra lại nhé.",
  self: "Không thể gắn mã giới thiệu của chính bạn.",
  cycle: "Không thể gắn mã này (người đó đang dùng mã của bạn).",
};

export function ReferralPromptDialog({ pathname }: { pathname: string }) {
  const { user } = useBoideUser();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [dontShow, setDontShow] = useState(false);
  const [binding, setBinding] = useState(false);
  const [error, setError] = useState("");
  const checkingRef = useRef(false);

  useEffect(() => {
    if (pathname === "/login" || !user?.username || !getBoideToken()) return;
    if (checkingRef.current || !takePending()) return;
    if (isNever(user.username)) return;
    checkingRef.current = true;
    // Gọi THẲNG, không qua cache bootstrap: `autoBindStoredReferral` vừa chạy trong
    // `finishLogin`, bản cache có thể là trạng thái TRƯỚC khi gắn → hỏi người vừa gắn xong.
    void getAffiliateMe()
      .then((me) => {
        if (me.has_referrer || me.referred_by_code) return;
        setCode(getStoredReferral() ?? "");
        setDontShow(false);
        setError("");
        setOpen(true);
      })
      .catch(() => {
        /* lỗi mạng/BE — lời mời không quan trọng bằng việc vào app trơn tru */
      })
      .finally(() => {
        checkingRef.current = false;
      });
  }, [pathname, user?.username]);

  function close() {
    if (dontShow && user?.username) setNever(user.username);
    setOpen(false);
  }

  async function submit() {
    const value = code.trim().toLowerCase();
    if (value.length < 3 || binding) return;
    setBinding(true);
    setError("");
    try {
      const res = await bindMyReferrer(value);
      clearStoredReferral();
      toast.success(`Đã gắn mã giới thiệu ${res.referred_by_code}`);
      setOpen(false);
    } catch (err) {
      const reason = referrerErrorReason(err);
      if (reason === "already_bound") {
        toast.info("Tài khoản của bạn đã gắn mã giới thiệu từ trước.");
        setOpen(false);
        return;
      }
      setError(
        (reason && REASON_TEXT[reason]) ||
          (err instanceof Error ? err.message : "Gắn mã thất bại, thử lại sau nhé."),
      );
    } finally {
      setBinding(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? setOpen(true) : close())}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <div className="mx-auto mb-1 flex h-11 w-11 items-center justify-center rounded-full bg-primary/15">
            <Gift className="h-5 w-5 text-primary" />
          </div>
          <DialogTitle className="text-center">Bạn có mã giới thiệu không?</DialogTitle>
          <DialogDescription className="text-center">
            Nhập mã của người đã giới thiệu bạn tới Bói Đê. Nếu bạn chưa dùng ưu đãi nạp lần đầu,
            lần nạp kế tiếp được cộng thêm 10%. Mã chỉ gắn được một lần.
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
          className="space-y-2"
        >
          <Input
            value={code}
            onChange={(e) => {
              setCode(e.target.value.replace(/\s+/g, "").toLowerCase());
              setError("");
            }}
            placeholder="Mã giới thiệu"
            autoCapitalize="none"
            autoComplete="off"
            spellCheck={false}
            aria-invalid={Boolean(error)}
          />
          {error ? <p className="text-xs text-destructive">{error}</p> : null}
          <Button
            type="submit"
            disabled={binding || code.trim().length < 3}
            className="w-full bg-gold-gradient text-primary-foreground"
          >
            {binding ? <Loader2 className="h-4 w-4 animate-spin" /> : "Gắn mã"}
          </Button>
        </form>

        <DialogFooter className="flex-col gap-3 sm:flex-col">
          <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
            <Checkbox checked={dontShow} onCheckedChange={(v) => setDontShow(v === true)} />
            Không hiện lại lần sau
          </label>
          <Button type="button" variant="ghost" className="w-full" onClick={close}>
            {dontShow ? "Đóng" : "Nhắc lại sau"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
